import { z } from 'zod'
import { isoDate, listQuerySchema, searchText } from './lists.js'

const email = z.string().trim().email('email must be valid').max(254)
const password = z.string().min(8, 'password must be at least 8 characters').max(128)
const personName = z.string().trim().max(100).optional()
/** Gruppo dell'utente; `null` = nessun gruppo (nessun permesso). */
const groupId = z.string().trim().min(1).max(64).nullable().optional()

/**
 * POST /api/users request body schema.
 */
export const CreateUserRequestSchema = z.object({
  email,
  password,
  firstName: personName,
  lastName: personName,
  groupId,
})

export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>

/** PUT /api/users/:id — `password` solo se va cambiata. */
export const UpdateUserRequestSchema = z.object({
  email,
  password: password.optional(),
  firstName: personName,
  lastName: personName,
  groupId,
  enabled: z.boolean(),
})

export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>

/** Giorni di validità del link d'invito. */
export const INVITE_DAYS = 7

/**
 * POST /api/users/invite: account senza password, in stato `pending` finché chi è invitato non la sceglie
 * dal link ricevuto via email (valido INVITE_DAYS giorni). L'invito si revoca eliminando l'utente.
 */
export const InviteUserRequestSchema = z.object({
  email,
  firstName: personName,
  lastName: personName,
  groupId,
  /** Lingua dell'email d'invito e della pagina in cui si sceglie la password. */
  lang: z.enum(['it', 'en']).default('it'),
})

export type InviteUserRequest = z.infer<typeof InviteUserRequestSchema>

/** POST /api/users/:id/invite: reinvia l'invito a un utente in attesa (nuovo link, il precedente smette di valere). */
export const ResendInviteRequestSchema = InviteUserRequestSchema.pick({ lang: true })

export type ResendInviteRequest = z.infer<typeof ResendInviteRequestSchema>

/** Utente come lo vede l'area gestionale (GET /api/users, /api/users/:id). */
export type AdminUser = {
  id: string
  email: string
  name: string
  firstName: string | null
  lastName: string | null
  enabled: boolean
  /** Invitato che non ha ancora scelto la password (nessun account credential). */
  pending: boolean
  /** Invitato in attesa senza più un link valido: va reinviato l'invito. */
  inviteExpired: boolean
  groupId: string | null
  groupName: string | null
  createdAt: string
}

/** GET /api/users: `state` e `group` (id, o `none`) sono filtri rapidi, gli altri campi la ricerca avanzata. */
export const UserListQuerySchema = listQuerySchema(['name', 'email', 'createdAt'], {
  state: z.enum(['active', 'disabled', 'pending', 'expired']).optional(),
  group: z.string().trim().min(1).max(64).optional(),
  email: searchText,
  firstName: searchText,
  lastName: searchText,
  createdFrom: isoDate,
  createdTo: isoDate,
})

export type UserListQuery = z.infer<typeof UserListQuerySchema>
